import {
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { type AuditEntry, useAuditLog } from '../../api/queries';

const ACTION_COLOR: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  create: 'success',
  update: 'info',
  delete: 'error',
};

function when(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function changeSummary(e: AuditEntry): string {
  if (e.action === 'create') return 'created';
  if (e.action === 'delete') return 'deleted';
  if (e.fields.length) {
    // Show before → after for each changed field (SGO 06/15 v2).
    return e.fields
      .map((f) => {
        const before = fmt(e.before?.[f]);
        const after = fmt(e.after?.[f]);
        return `${f}: ${before} → ${after}`;
      })
      .join('; ');
  }
  return '';
}

function fmt(v: unknown): string {
  if (v === null || v === undefined || v === '') return '∅';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export default function AdminAudit() {
  const { data, isLoading } = useAuditLog();

  return (
    <Stack spacing={2}>
      <div>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Activity log
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Every admin and data change, newest first — who changed what, and when.
        </Typography>
      </div>

      {isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Who</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Resource</TableCell>
              <TableCell>Changed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data ?? []).map((e, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(e.at)}</TableCell>
                <TableCell>{e.actor}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={e.action}
                    color={ACTION_COLOR[e.action] ?? 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={e.resource_type}>
                    <span>{e.resource_id}</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{changeSummary(e)}</TableCell>
              </TableRow>
            ))}
            {(data ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    No activity recorded yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Stack>
  );
}
